import { ChangeDetectionStrategy, Component, computed, effect, inject, input, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import { DATE_INVALID_FORMAT, NAME_TOO_SHORT } from '@config/error-messages.const';
import { CommonValidators } from 'app/shared/validators/common-validators';
import { distinctUntilChanged, startWith } from 'rxjs';
import { Insurant } from '@models/insurant';
import { validateBirthday } from '../../../shared/validators/date.validator';
import { OrderStepBaseComponent } from '../../order-step-base-component';
import { OrderState } from 'app/order/models/order-state.model';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationButtons } from '../navigation-buttons/navigation-buttons.component';
import { RequiredInfoComponent } from 'app/shared/components/required-info/required-info.component';
import { ReactiveLabelDirective } from 'app/shared/directives/reactive-label.directive';
import { CtrlDateInputDirective, CtrlErrorMessageDirective } from '@co-3/ctrl-ui/forms';

@Component({
    selector: 'app-single-insurant-form',
    imports: [
        ReactiveFormsModule,
        RequiredInfoComponent,
        ReactiveLabelDirective,
        NavigationButtons,
        CtrlDateInputDirective,
        CtrlErrorMessageDirective,
    ],
    templateUrl: './single-insurant-form.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SingleInsurantFormComponent
    extends OrderStepBaseComponent<Pick<OrderState, 'insurants' | 'insurantsFamilyRelationship'>>
    implements OnInit
{
    public insurants = input.required<Insurant[]>();

    // #region errormessages
    protected firstNameErrorMessages = NAME_TOO_SHORT;
    protected lastNameErrorMessages = NAME_TOO_SHORT;
    protected dateOfBirthErrorMessages = {
        required: DATE_INVALID_FORMAT,
        ageTooHigh: 'Die versicherte Person darf nicht Älter als 18 Jahre sein.',
        ageTooLow: 'Das Geburtsdatum darf nicht in der Zukunft liegen.',
        invalidDateFormat: DATE_INVALID_FORMAT,
    };
    // #endregion

    public form = inject(FormBuilder).group({
        isInsuree: [true],
        isChild: [false],
        firstName: [''],
        lastName: [''],
        dateOfBirth: [''],
    });

    protected insurantIsInsuree = toSignal(
        this.form.controls.isInsuree.valueChanges.pipe(
            startWith(this.form.controls.isInsuree.value),
            distinctUntilChanged(),
        ),
    );

    protected showInsurantForm = computed(() => !this.insurantIsInsuree());

    protected insurantMaxAge = computed(() => this.product().orderConfig.insurantMaxAge || 18);

    protected setFamilyRelationshipOnSingleInsurant = computed(
        () => this.product().orderConfig.setFamilyRelationshipOnSingleInsurant,
    );

    public constructor() {
        super();

        // update validators depending on wether the insurant is the insuree or not
        effect(() => {
            if (!this.insurantIsInsuree()) {
                this.setValidator('firstName', CommonValidators.shortSomething);
                this.setValidator('lastName', CommonValidators.shortSomething);
                this.setValidator('dateOfBirth', [Validators.required, validateBirthday(0, 18)]);
            } else {
                this.setValidator('firstName', []);
                this.setValidator('lastName', []);
                this.setValidator('dateOfBirth', []);
            }
        });
    }

    public ngOnInit(): void {
        if (this.insurants().length) {
            this.form.patchValue({ ...this.insurants().at(0) });
        }
        if (this.insurantMaxAge() < 19) {
            this.form.patchValue({ isInsuree: false, isChild: true });
        }
    }

    private setValidator(
        formControlName: string,
        newValidator: ValidatorFn | ValidatorFn[],
        emitEvent: boolean = true,
    ): void {
        const fc = this.form.get(formControlName);
        if (fc !== null) {
            fc.setValidators(newValidator);
            fc.updateValueAndValidity({ emitEvent });
        }
    }

    private getInsurants(): Insurant[] {
        if (this.form.controls.isInsuree.value === true) {
            return [{ isInsuree: true, isChild: false, firstName: '', lastName: '', dateOfBirth: '' }];
        } else {
            return [{ ...this.form.getRawValue(), isChild: true }];
        }
    }

    public getData(): Pick<OrderState, 'insurants' | 'insurantsFamilyRelationship'> {
        const insurantsFamilyRelationship =
            this.form.get('isInsuree')?.value === false && this.setFamilyRelationshipOnSingleInsurant() === true;
        return {
            insurants: this.getInsurants(),
            insurantsFamilyRelationship,
        };
    }
}
